<template>
  <header
    ref="widgetHeader"
    class="octadesk-app-header text-center octadesk--header"
    :class="{
      'in-conversation-padding-bottom': chat && !showKbCard ? true : false,
      'agents-thumb-padding-offset': canShowAgentsThumb,
      'header-kb': showKbCard
    }"
    :style="`background-image: linear-gradient(to top, ${lightenColor(
      rgbToHex(settings.bgColorRGB),
      20
    )}, rgba(${settings.bgColorRGBA})); color: rgba(${
      settings.fontColorRGBA
    });`"
  >
    <div class="octadesk-app-header-apresentation">
      <p
        v-if="hasTitle"
        :style="`color: rgba(${settings.fontColorRGBA});`"
        class="octadesk-header--title"
        :class="[!settings.introduction ? 'no-introduction' : '']"
      >
        {{ settings.title }}
      </p>

      <p
        class="octadesk-header--introduction"
        :style="`color: rgba(${settings.fontColorRGBA});`"
      >
        {{ settings.introduction }}
      </p>

      <div v-if="canShowAgentsThumb" class="agent-thumbs-container">
        <div
          v-for="agent in filteredOnlineAgents"
          :key="agent.id"
          class="agent-avatar--container"
        >
          <div class="octadesk-app-avatar avatar--small">
            <img
              v-if="agent.thumbUrl"
              v-tooltip="agent.displayName || agent.name"
              :src="agent.thumbUrl.replace('http:', 'https:')"
            />

            <div
              v-else
              v-tooltip="agent.displayName || agent.name"
              class="
                octadesk-app-disc
                widget-header
                d-flex
                align-items-center
                justify-content-center
              "
              :style="`color: rgba(${settings.bgColorRGBA}); background-color: rgba(${settings.fontColorRGBA}`"
            >
              <p v-text="agent.displayName[0] || agent.name[0]"></p>
            </div>

            <div
              style="
                position: absolute;
                right: 0;
                bottom: 0;
                width: 11px;
                height: 11px;
                padding: 3px;
                border-radius: 100%;
                background: #fff;
              "
            >
              <div
                style="width: 6px; height: 6px; border-radius: 100%"
                :class="connectionStatus()"
              ></div>
            </div>
          </div>

          <p class="agent-avatar--name">
            {{ agent.displayName.split(' ')[0] || agent.name.split(' ')[0] }}
          </p>
        </div>
      </div>
    </div>

    <button
      v-if="canShowCloseButton"
      :style="`color: rgba(${settings.fontColorRGBA});`"
      type="button"
      class="octadesk-app-header-close"
      title="close"
      aria-label="Close"
      @click="toggleApp"
    >
      <svg viewBox="0 0 1024 1024" width="13" height="13">
        <use xlink:href="#octadesk-app-icon-close" />
      </svg>
    </button>
  </header>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex'

  import { getRandomItemsFromArray } from '@/helpers'

  export default {
    data() {
      return {
        isMobile: window.innerWidth < 768
      }
    },
    computed: {
      ...mapGetters([
        'settings',
        'isPopup',
        'chat',
        'onlineAgents',
        'showKbCard',
        'chatStatus',
        'kbEnabled',
        'visibleButton'
      ]),
      canShowAgentsThumb() {
        return (
          this.settings.showAgentsThumb &&
          this.filteredOnlineAgents &&
          !this.showKbCard &&
          this.inConversationRoute
        )
      },
      canShowCloseButton() {
        return !this.isPopup && (this.isMobile || !this.visibleButton)
      },
      inConversationRoute() {
        return this.$route && this.$route.path === '/conversation'
      },
      hasTitle() {
        return this.settings && this.settings.title.trim().length !== 0
      },
      filteredOnlineAgents() {
        if (this.onlineAgents) {
          if (this.onlineAgents.length <= 4) {
            return [...this.onlineAgents]
          } else {
            return getRandomItemsFromArray(this.onlineAgents, 4)
          }
        } else {
          return []
        }
      }
    },
    watch: {
      chatStatus(status) {
        this.getHeaderHeight()

        const STARTED_STATUS = 4

        if (status === STARTED_STATUS) {
          this.handleGetOnlineAgents()
        }
      },
      showKbCard(status) {
        if (!status) {
          setTimeout(() => {
            this.getHeaderHeight()
            this.handleGetOnlineAgents()
          }, 0)
        }
      }
    },
    updated() {
      this.getHeaderHeight()
    },
    mounted() {
      this.getHeaderHeight()

      this.subscribeWindowResize()

      if (
        this.inConversationRoute &&
        (!this.chat || !this.chat.agent) &&
        !this.kbEnabled
      ) {
        setTimeout(() => {
          this.handleGetOnlineAgents()
        }, 100)
      }
    },
    beforeDestroy() {
      this.unsubscribeWindowResize()
    },
    methods: {
      ...mapActions(['toggleApp', 'updateHeaderHeight', 'getOnlineAgents']),
      handleGetOnlineAgents() {
        if (this.settings.showAgentsThumb && !this.onlineAgents) {
          this.getOnlineAgents().then(() => {
            this.getHeaderHeight()
          })
        }
      },
      getHeaderHeight() {
        this.$nextTick(() => {
          if (this.$refs.widgetHeader) {
            const styles = window.getComputedStyle(this.$refs.widgetHeader)

            const margin =
              parseFloat(styles['marginTop'].replace('px', '')) +
              parseFloat(styles['marginBottom'].replace('px', ''))

            const headerHeight = Math.ceil(
              this.$refs.widgetHeader.offsetHeight + margin
            )

            this.updateHeaderHeight(headerHeight)
          }
        })
      },
      rgbToHex(rgb) {
        const hex = rgb.split(',').map(color => {
          let hexColor = Number(color).toString(16)

          if (hexColor.length < 2) {
            hexColor = '0' + hexColor
          }

          return hexColor
        })
        return hex.join('')
      },
      lightenColor(color, percentage) {
        const num = parseInt(color.replace('#', ''), 16)

        const amt = Math.round(2.55 * percentage)

        const R = (num >> 16) + amt

        const B = ((num >> 8) & 0x00ff) + amt

        const G = (num & 0x0000ff) + amt

        return (
          '#' +
          (
            0x1000000 +
            (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
            (B < 255 ? (B < 1 ? 0 : B) : 255) * 0x100 +
            (G < 255 ? (G < 1 ? 0 : G) : 255)
          )
            .toString(16)
            .slice(1)
        )
      },
      connectionStatus() {
        return 'online'
      },
      subscribeWindowResize() {
        this.$nextTick(function () {
          window.addEventListener('resize', this.getHeaderHeight)
        })
      },
      unsubscribeWindowResize() {
        window.removeEventListener('resize', this.getHeaderHeight)
      }
    }
  }
</script>

<style lang="scss">
  .octadesk-app-header {
    height: auto !important;
    font-family: Arial, 'Helvetica Neue', Helvetica, sans-serif !important;
    border-radius: 8px 8px 0px 0px;
    padding: 20px 41px 45px 41px !important;
    @media (max-height: 700px) {
      padding-top: 15px !important;
    }
    @media (max-height: 700px) {
      padding: 15px 37px 45px 16px !important;
    }
    &.in-conversation-padding-bottom {
      padding-bottom: 20px !important;
    }
  }

  .octadesk-app-header.no-agents {
    padding-bottom: 40px !important;
  }

  .agents-thumb-padding-offset {
    padding-bottom: 1.2%;
  }

  .octadesk-app-header-close {
    position: absolute;
    top: 13px;
    right: 5.2%;
    margin: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
    outline: none;
  }

  .octadesk-app-header-name {
    text-align: left;
    font-weight: bold;
    line-height: inherit;
    margin: 0 0 5px 0 !important;
  }

  .octadesk-app-header-name.no-thumbs,
  .octadesk-app-contact .octadesk-app-header-name {
    width: 100%;
  }

  .octadesk-app-header-name.empty {
    display: block;
    min-height: 3.125rem;
  }

  .thumb-header-apresentation {
    width: 42px;
    height: 42px;
    border-radius: 100%;
  }

  .agent-name-overflow {
    overflow: hidden;
    display: block;
    max-width: 100px;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-top: 5px;
  }

  .margin-with-thumb {
    margin-top: 28px;
  }

  .namedThumb {
    font-size: 21px;
    padding-top: 9px;
  }

  .octadesk-app-header-apresentation {
    display: block;
    text-align: left;
  }

  .agent-thumbs-container {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-top: 11px;
  }

  .octa-chat-introduction {
    text-align: left;
    font-size: 12px;
    margin: 0 0 0 0;
  }

  .agent-thumb-item {
    display: block;
    float: left;
    width: 42px;
    height: 42px;
    font-size: 12px;
    margin-right: -15px;
  }

  .agent-thumb-item:last-child {
    margin-right: 0;
  }

  .octadesk-header--title {
    font-family: Roboto;
    font-size: 25px;
    line-height: 28px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    letter-spacing: normal;
    margin: 0;
    margin-bottom: 0.8125rem;
    max-height: 86px;
    overflow: hidden;
    @media (max-height: 600px) {
      margin-bottom: 0px;
    }
  }
  .octadesk-header--title.no-introduction {
    margin-bottom: 0px;
  }

  .octadesk-header--introduction {
    font-family: Roboto;
    font-size: calc(14px + (16 - 14) * ((100vw - 100px) / (1920 - 100)));
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    overflow: hidden;
    margin: 0;
    @media (max-height: 600px) {
      display: none;
    }
  }

  .avatar--small {
    width: 39px;
    height: 39px;
    font-size: 25px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: visible;
  }

  .online {
    background-color: #50b848;
  }

  @media screen and (max-height: 500px) {
    .octadesk-header--introduction {
      display: none;
    }
  }

  .agent-avatar--container {
    display: flex;
    align-items: center;
    flex-direction: column;
    text-align: center;
    margin-right: 6.6%;
  }

  .agent-avatar--name {
    font-family: Roboto;
    font-size: 14px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    margin: 0;
    margin-top: 4px;
    color: #fff;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 60px;
    white-space: nowrap;
  }

  .in-conversation-padding-bottom {
    padding-bottom: 9px;
  }
</style>
